import { useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import Turnstile from '../components/Turnstile';

export default function VendorAcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['vendor', 'common']);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const handleTurnstileVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileError = useCallback(() => {
    setError('Verification failed. Please refresh and try again.');
    setTurnstileToken('');
  }, []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(''), []);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh' ? 'en' : 'zh');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) { setError('Invalid invite link'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!turnstileToken) { setError('Please complete the verification challenge'); return; }

    setLoading(true);
    try {
      await api.vendorAcceptInvite(token, password, confirmPassword, turnstileToken);
      await refresh();
      navigate('/vendor/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0B0B0B' }}>
      <div className="w-full max-w-[400px] animate-fade-in">
        <div className="mb-10">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-baseline gap-0">
              <span className="font-display font-bold text-xl text-white" style={{ letterSpacing: '-0.02em' }}>CreatorHub</span>
              <span className="font-display font-bold text-xl" style={{ color: '#FFD400', letterSpacing: '-0.02em' }}>360</span>
            </Link>
            <button
              onClick={toggleLang}
              className="text-xs px-2.5 py-1 transition-colors"
              style={{ color: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}
            >
              {t('vendor:login.switchLang')}
            </button>
          </div>
          <h1 className="text-2xl font-display font-bold text-white mb-1" style={{ letterSpacing: '-0.02em' }}>
            {t('vendor:acceptInvite.title')}
          </h1>
          <p className="text-sm text-white/45">{t('vendor:acceptInvite.subtitle')}</p>
        </div>

        <div
          className="p-7 space-y-5"
          style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px' }}
        >
          {error && (
            <div className="p-3 text-sm text-red-400" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.18)', borderRadius: '2px' }}>
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="password" className="block text-xs font-semibold text-white/50 uppercase tracking-wider">
              {t('common:newPassword')}
            </label>
            <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="input" placeholder="••••••••" required autoComplete="new-password" />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="confirmPassword" className="block text-xs font-semibold text-white/50 uppercase tracking-wider">
              {t('common:confirmPassword')}
            </label>
            <input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="input" placeholder="••••••••" required autoComplete="new-password" />
          </div>

          <Turnstile onVerify={handleTurnstileVerify} onError={handleTurnstileError} onExpire={handleTurnstileExpire} />

          <button
            type="button"
            onClick={handleSubmit as unknown as React.MouseEventHandler}
            disabled={loading || !turnstileToken}
            className="btn-primary w-full"
          >
            {loading ? t('vendor:acceptInvite.settingUp') : t('vendor:acceptInvite.setPassword')}
          </button>
        </div>
      </div>
    </div>
  );
}
